import { MAP_WIDTH, MAP_HEIGHT } from "./constants.js";
import {
  Terrain,
  type MapBase,
  type MapPillbox,
  type MapStart,
} from "./map.js";

// Serialiser for the WinBolo .map binary format. Inverse of
// server/src/game/winboloMap.ts. Lives in shared so the in-browser
// map builder can produce bytes that round-trip through the same
// parser the server already uses.
//
// Layout (mirrors the parser):
//   8   bytes  "BMAPBOLO"
//   1   byte   version = 1
//   1   byte   pill count P
//   1   byte   base count B
//   1   byte   start count S
//   P*5 bytes  pillbox records  { x, y, owner, armor, speed }
//   B*6 bytes  base records     { x, y, owner, armor, shells, mines }
//   S*3 bytes  start records    { x, y, dir }
//   ...        terrain runs, ended by { 04 FF FF FF }

export interface BuilderMap {
  terrain: Uint8Array; // length MAP_WIDTH * MAP_HEIGHT
  pillboxes: MapPillbox[];
  bases: MapBase[];
  starts: MapStart[];
  mines?: { x: number; y: number }[];
}

const MAGIC = "BMAPBOLO";
const END_MARKER = new Uint8Array([4, 0xff, 0xff, 0xff]);

// Our local Terrain enum -> WinBolo nibble (sans mine overlay).
// Pillbox/Base tiles in the grid are written as Grass; the entity
// itself is recorded in pillboxes[] / bases[] and the parser overlays
// the tile back on load.
function terrainToNibble(t: Terrain): number {
  switch (t) {
    case Terrain.Wall:          return 0;  // Building
    case Terrain.River:         return 1;
    case Terrain.Swamp:         return 2;
    case Terrain.Crater:        return 3;
    case Terrain.Road:          return 4;
    case Terrain.Forest:        return 5;
    case Terrain.Rubble:        return 6;
    case Terrain.Grass:         return 7;
    case Terrain.HalfBuiltWall: return 8;  // Shot Building
    case Terrain.Boat:          return 9;
    case Terrain.Pillbox:       return 7;  // entity in pillboxes[]
    case Terrain.Base:          return 7;  // entity in bases[]
    case Terrain.BoatOrBuilding: return 0; // map to Building
    // DeepSea isn't representable as a nibble — caller must keep it
    // outside the [sx, ex) range. We fall through to building as a
    // safe non-passable default if anyone slips one in.
    case Terrain.DeepSea:       return 0;
    default:                    return 7;
  }
}

// Nibble overlay for the same terrain with an authored mine
// (parser indices 10..15). Returns -1 if the terrain can't carry a
// mine in the WinBolo format.
function terrainMineNibble(t: Terrain): number {
  switch (t) {
    case Terrain.Swamp:  return 10;
    case Terrain.Crater: return 11;
    case Terrain.Road:   return 12;
    case Terrain.Forest: return 13;
    case Terrain.Rubble: return 14;
    case Terrain.Grass:  return 15;
    default:             return -1;
  }
}

// WinBolo direction encoding: 0 = East, steps CCW.
// Ours: 0 = North, steps CW. The conversion is self-inverse.
function localDirToWinbolo(local: number): number {
  return ((4 - local) % 16 + 16) % 16;
}

// Owner 0 in our model = neutral; WinBolo writes 0xff for that.
function ownerToWinbolo(owner: number): number {
  return owner === 0 ? 0xff : owner & 0xff;
}

export function writeWinboloMap(map: BuilderMap): Uint8Array {
  if (map.terrain.length !== MAP_WIDTH * MAP_HEIGHT) {
    throw new Error(`terrain length ${map.terrain.length} != ${MAP_WIDTH * MAP_HEIGHT}`);
  }
  if (map.pillboxes.length > 255 || map.bases.length > 255 || map.starts.length > 255) {
    throw new Error("entity list exceeds 1-byte count limit");
  }

  // Build a quick lookup of authored mine positions for nibble overlay.
  const mineAt: Uint8Array = new Uint8Array(MAP_WIDTH * MAP_HEIGHT);
  for (const m of map.mines ?? []) {
    if (m.x >= 0 && m.x < MAP_WIDTH && m.y >= 0 && m.y < MAP_HEIGHT) {
      mineAt[m.y * MAP_WIDTH + m.x] = 1;
    }
  }

  const chunks: Uint8Array[] = [];

  // Header.
  const header = new Uint8Array(12);
  for (let i = 0; i < 8; i++) header[i] = MAGIC.charCodeAt(i);
  header[8] = 1; // version
  header[9] = map.pillboxes.length;
  header[10] = map.bases.length;
  header[11] = map.starts.length;
  chunks.push(header);

  // Pillboxes.
  const pillBuf = new Uint8Array(map.pillboxes.length * 5);
  for (let i = 0; i < map.pillboxes.length; i++) {
    const p = map.pillboxes[i];
    const o = i * 5;
    pillBuf[o] = p.x & 0xff;
    pillBuf[o + 1] = p.y & 0xff;
    pillBuf[o + 2] = ownerToWinbolo(p.owner);
    pillBuf[o + 3] = p.armor & 0xff;
    pillBuf[o + 4] = p.speed & 0xff;
  }
  chunks.push(pillBuf);

  // Bases.
  const baseBuf = new Uint8Array(map.bases.length * 6);
  for (let i = 0; i < map.bases.length; i++) {
    const b = map.bases[i];
    const o = i * 6;
    baseBuf[o] = b.x & 0xff;
    baseBuf[o + 1] = b.y & 0xff;
    baseBuf[o + 2] = ownerToWinbolo(b.owner);
    baseBuf[o + 3] = b.armor & 0xff;
    baseBuf[o + 4] = b.shells & 0xff;
    baseBuf[o + 5] = b.mines & 0xff;
  }
  chunks.push(baseBuf);

  // Starts.
  const startBuf = new Uint8Array(map.starts.length * 3);
  for (let i = 0; i < map.starts.length; i++) {
    const s = map.starts[i];
    const o = i * 3;
    startBuf[o] = s.x & 0xff;
    startBuf[o + 1] = s.y & 0xff;
    startBuf[o + 2] = localDirToWinbolo(s.direction) & 0xff;
  }
  chunks.push(startBuf);

  // Terrain runs. Walk each row, emit one or more runs that skip past
  // contiguous spans of DeepSea (which can't be represented inside a
  // run — outside [sx, ex) is implicit deep sea on load).
  for (let y = 0; y < MAP_HEIGHT; y++) {
    let x = 0;
    while (x < MAP_WIDTH) {
      // Skip leading deep sea.
      while (x < MAP_WIDTH && map.terrain[y * MAP_WIDTH + x] === Terrain.DeepSea) x++;
      if (x >= MAP_WIDTH) break;
      const sx = x;
      while (x < MAP_WIDTH && map.terrain[y * MAP_WIDTH + x] !== Terrain.DeepSea) x++;
      const ex = x;

      // Build the nibble list for tiles in [sx, ex).
      const tileNibbles: number[] = [];
      for (let i = sx; i < ex; i++) {
        const t = map.terrain[y * MAP_WIDTH + i] as Terrain;
        const baseN = terrainToNibble(t);
        const mineN = mineAt[y * MAP_WIDTH + i] ? terrainMineNibble(t) : -1;
        tileNibbles.push(mineN >= 0 ? mineN : baseN);
      }

      const dataNibbles = rleEncodeNibbles(tileNibbles);
      // Pack nibbles into bytes, high nibble first; pad with 0 if odd.
      const dataBytes = new Uint8Array(Math.ceil(dataNibbles.length / 2));
      for (let i = 0; i < dataNibbles.length; i++) {
        if ((i & 1) === 0) dataBytes[i >> 1] = (dataNibbles[i] & 0xf) << 4;
        else dataBytes[i >> 1] |= dataNibbles[i] & 0xf;
      }
      const datalen = 4 + dataBytes.length;
      if (datalen > 0xff) {
        // Format limit; should be unreachable for 256-wide rows under
        // any encoding scheme, but bail rather than silently truncating.
        throw new Error(`row ${y} run too large: ${datalen} bytes`);
      }
      const runHeader = new Uint8Array(4);
      runHeader[0] = datalen;
      runHeader[1] = y;
      runHeader[2] = sx;
      runHeader[3] = ex;
      chunks.push(runHeader);
      chunks.push(dataBytes);
    }
  }

  chunks.push(END_MARKER);

  // Concatenate.
  let total = 0;
  for (const c of chunks) total += c.length;
  const out = new Uint8Array(total);
  let o = 0;
  for (const c of chunks) { out.set(c, o); o += c.length; }
  return out;
}

// Greedy RLE: prefer repeat-mode (2..9 reps) over sequence-mode (1..8
// literals) whenever the next two tiles match. Mirrors the decoder in
// winboloMap.ts so round-trip is byte-stable for typical maps.
function rleEncodeNibbles(tiles: number[]): number[] {
  const out: number[] = [];
  let i = 0;
  while (i < tiles.length) {
    // Count run of identical nibbles starting at i.
    let j = i;
    while (j < tiles.length && j - i < 9 && tiles[j] === tiles[i]) j++;
    const runLen = j - i;
    if (runLen >= 2) {
      // Repeat mode: code nibble = reps + 6, then the tile nibble.
      out.push(runLen + 6); // 8..15
      out.push(tiles[i]);
      i = j;
      continue;
    }
    // Sequence mode: gather literals until we hit a >=2 run or the
    // 8-literal cap.
    let k = i + 1;
    while (k < tiles.length && k - i < 8) {
      if (k + 1 < tiles.length && tiles[k] === tiles[k + 1]) break;
      k++;
    }
    const count = k - i;
    out.push(count - 1); // 0..7
    for (let p = i; p < k; p++) out.push(tiles[p]);
    i = k;
  }
  return out;
}
